(ns ui.view.card.associated-entities.associated-entities-table
  (:require
   [ui.util :as uu]
   [clojure.string :as string]
   [reagent.core :as r]
   [common.util :as cu]
   [oops.core :as oops]
   [ui.components.grid.view :refer [ag-grid-enterprise theme-quartz]]
   [ui.components.radix-ui.accordion.view :refer [accordion-primitive-root
                                                  accordion-primitive-header
                                                  accordion-primitive-trigger
                                                  accordion-primitive-content
                                                  accordion-primitive-item]]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.view.card.associated-entities.sub :as sub]
   [ui.view.card.associated-entities.event :as event]
   [ui.view.card.event :as common-event]
   [common.schema.entity-type-names :as entity-type-names]
   [common.localization :as loc]
   [ui.components.toggle.view :as toggle]
   [re-frame.core :as rf]))

(defn accordion-item
  [props & children]
  (let [children (if (map? props) children (cons props children))
        props (if (map? props) props {})]
    [accordion-primitive-item
     props
     (into [:<>] children)]))

(defn accordion-content
  [& children]
  [accordion-primitive-content
   ;; For some reason the animation would not work written in tailwind css
   {:class "AssociatedEntitiesTableAccordionContent"}
   (into [:div {:class "pt-0 w-full grow"}] children)])

(defn column-value-getter
  [field row-obj]
  (let [field-str (cu/kw->str field)
        value (some-> row-obj
                      (oops/oget "data")
                      (oops/oget+ field-str)
                      (js->clj :keywordize-keys true))
        display-value (if (coll? value)
                        (->> value
                             (mapv :label)
                             (string/join "; "))
                        value)]
    display-value))

(defn action-column-cell-renderer
  [{:keys [ctx]} js-args]
  (let [args (js->clj js-args :keywordize-keys true)
        data (:data args)
        associated-entity-id (:entity-id data)
        association-id (:association-id data)
        comment-attribute (-> data
                              :comment-attribute
                              (cu/str->keyword-if-string))
        comment (:comment data)
        open-edit-comment-modal #(rf/dispatch [::event/open-edit-comment-modal ctx {:association-id association-id
                                                                                    :associated-entity-id associated-entity-id
                                                                                    :comment comment
                                                                                    :comment-attribute comment-attribute}])
        remove-association #(rf/dispatch [::event/remove-association ctx {:association-id association-id
                                                                          :associated-entity-id associated-entity-id}])]
    (r/as-element
     [:div
      {:class "flex flex-col justify-center items-center h-full w-full"
       :on-mouse-enter (fn [e]
                         (oops/ocall e "preventDefault")
                         (oops/ocall e "stopPropagation"))}
      [dropdown-menu/view
       {:items [{:label (loc/tr :edit-comment)
                 :icon-class "fal fa-pencil"
                 :on-click open-edit-comment-modal}
                {:label (loc/tr :remove)
                 :icon-class "fal fa-trash-alt"
                 :on-click remove-association}]
        :props {:hide-arrow? true
                :align :end}
        :trigger-as-child
        [:button
         {:class "hover:bg-base-regular h-8 w-8 p-0 rounded"}
         [:i.fal.fa-ellipsis-h
          {:class "text-lg font-medium text-base-light-text-paler"}]]}]])))

(defn create-column-defs
  [{:keys [columns sort-by-field sort-model sort-direction
           group-by-association-type?] :as props}]
  (let [sort-fields-set (->> sort-model
                             (mapv :field)
                             set)
        sort-infos-by-field (->> sort-model
                                 (map-indexed
                                  (fn [idx {:keys [field sort-direction]}]
                                    [field {:sort-index idx
                                            :sort-direction sort-direction}]))
                                 (into {}))
        data-columns (->> columns
                          (map-indexed
                           (fn [col-ord {:keys [label field]}]
                             (let [last-column? (= col-ord (dec (count columns)))
                                   group? (and group-by-association-type?
                                               (= field :association-type))]
                               (merge
                                {:headerName label
                                 :flex (when last-column? 1)
                                 :sort (when (= field sort-by-field)
                                         sort-direction)
                                 :field (cu/kw->str field)
                                 :minWidth (when last-column? 200)
                                 :rowGroup group?
                                 :hide group?
                                 :valueGetter (partial column-value-getter field)}
                                (when (sort-fields-set field)
                                  {:sort (get-in sort-infos-by-field [field :sort-direction])
                                   :sortIndex (get-in sort-infos-by-field [field :sort-index])})))))
                          (vec))
        action-column {:headerName ""
                       :pinned :right
                       :resizable false
                       :sortable false
                       :width 60
                       :cellRenderer (partial action-column-cell-renderer props)
                       :cellStyle {:padding "0px"}
                       :cellClass (uu/cm "border-r-transparent focus:border-y-transparent focus:border-l-[var(--ag-cell-horizontal-border)] focus:outline-none"
                                         "focus-within:border-y-transparent focus-within:border-l-[var(--ag-cell-horizontal-border)] focus-within:outline-none")}
        all-columns (conj data-columns action-column)]
    all-columns))

(defn row-data->ag-row-data
  [row-data]
  (->> row-data
       (mapv (fn [item]
               (->> item
                    (mapv (fn [[field value]]
                            (let [value (if (= field :comment-attribute)
                                          (cu/kw->str value)
                                          value)]
                              [(cu/kw->str field) value])))
                    (into {}))))))

(defn group-by-role-switch
  [{:keys [id checked? on-change]}]
  [:div
   {:class "flex gap-2"}
   [:label
    {:for id}
    (loc/tr :group-by-association-type)]

   [toggle/toggle
    {:id id
     :checked? checked?
     :on-change (fn [checked?]
                  (on-change checked?))}]])

(defn header
  [{:keys [table table-index ctx] :as _props}]
  (let [title (:title table)
        row-count @(rf/subscribe [::sub/table-entity-count ctx table-index])]
    [accordion-primitive-header
     {:asChild true}
     [:div
      {:class "flex justify-start data-[state=closed]"}
      [accordion-primitive-trigger
       {:class (uu/cm "group p-0 flex flex-row gap-3 items-center flex-1 m-0 transition-all"
                      "[&[data-state=open]>i]:rotate-90")}
       [:div
        {:class "flex items-center gap-3 "}
        [:div
         {:class "flex items-center gap-1"}
         [:p
          {:class "text-md font-semibold transition-opacity group-hover:text-base-light-text-accent"} title]
         [:p
          {:class "w-5 h-5 flex justify-center items-center text-xs transition-opacity group-hover:text-base-light-text-accent bg-base-regular rounded-md"}
          row-count]]]

       [:i.fal.fa-angle-right
        {:class "text-xl text-neutral-700 transition-all group-hover:text-base-light-text-accent"}]]]]))

(defn table-header-tools
  [{:keys [id grouped? set-grouped]} {}]
  (let []
    [:div
     {:class "flex items-center justify-between"}
     [:div]
     ;; Disabled for now since row grouping is broken on this table.
     ;; Needs more investigation why does not work. Could be issue with css or something else.
     #_[group-by-role-switch
        {:id id
         :checked? grouped?
         :on-change set-grouped}]]))

(defn table-component
  [{:keys [ctx table-index]}]
  (let [grid-ref-atom (r/atom nil)

        row-group-expanded-states @(rf/subscribe [::sub/table-row-group-expanded-states ctx table-index])
        row-group-expanded-states-local* (r/atom row-group-expanded-states)]
    (fn [{:keys [ctx table-index] :as props}]

      (let [columns @(rf/subscribe [::sub/table-columns ctx table-index])
            sort-model @(rf/subscribe [::sub/table-sort-model ctx table-index])
            group-by-association-type? @(rf/subscribe [::sub/table-group-by-association-type? ctx table-index])

            col-defs (create-column-defs
                      (assoc props
                             :columns columns
                             :sort-model sort-model
                             :group-by-association-type? group-by-association-type?))

            row-data @(rf/subscribe [::sub/row-data ctx table-index])
            ag-row-data (row-data->ag-row-data row-data)

            set-grouped (fn [_grouped?]
                          (rf/dispatch-sync [::event/toggle-table-group-by-association-type ctx table-index]))

            on-cell-clicked (fn [args])]
        [:<>
         [:div
          {:class "mb-2"}
          [table-header-tools
           {:id (str "association-group-by-" table-index)
            :grouped? group-by-association-type?
            :set-grouped set-grouped}]]
         [:div
          {:class "h-full"}
          [:div
           {:class "h-full w-full"}
           [ag-grid-enterprise
            (merge
             {:theme theme-quartz
              :rowData ag-row-data
              :ref (fn [ref]
                     (reset! grid-ref-atom ref))
              :columnDefs col-defs
              :suppressContextMenu true
              :defaultColDef {:sortable true
                              :suppressHeaderMenuButton true
                              :suppressColumnsToolPanel true
                              :suppressFiltersToolPanel true
                              :autoSize true
                              :resizable true
                              :suppressMovable true
                              :menuTabs []
                              :onCellClicked (fn [args]
                                               (when-let [entity-id (some-> args
                                                                            (oops/oget "data")
                                                                            (oops/oget "entity-id"))]
                                                 (rf/dispatch [::common-event/open-entity-peek-or-close-if-already-open ctx entity-id])))}
              :isGroupOpenByDefault (fn [args]
                                      (let [group-key (oops/oget args "key")
                                            expanded? (get @row-group-expanded-states-local* group-key)]
                                        expanded?))
              :onSortChanged (fn [args]
                               (let [column-state (-> args
                                                      (oops/oget "api")
                                                      (oops/ocall "getColumnState")
                                                      (js->clj :keywordize-keys true))
                                     sort-model (->> column-state
                                                     (filterv :sort)
                                                     (sort-by :sortIndex)
                                                     (mapv (fn [{:keys [colId sort]}]
                                                             {:field (keyword colId)
                                                              :sort-direction  (keyword sort)})))]
                                 (rf/dispatch-sync [::event/set-table-sort-model ctx {:table-index table-index
                                                                                      :sort-model sort-model}])))
              :onRowGroupOpened (fn [args]
                                  (let [node (oops/oget args "node")
                                        group-key (oops/oget node "key")
                                        expanded? (oops/oget node "expanded")]
                                    (swap! row-group-expanded-states-local* assoc group-key expanded?)
                                    (rf/dispatch-sync [::event/set-table-row-group-expanded ctx {:table-index table-index
                                                                                                 :group-key group-key
                                                                                                 :expanded? expanded?}])))
              :suppressRowHoverHighlight true
              :suppressHorizontalScroll true
              :groupDefaultExpanded 0
              :groupDisplayType "groupRows"
              :domLayout :autoHeight
              :autoSizeStrategy {:type :fitCellContents}
              :suppressFiltersToolPanel true})]]]]))))

(defn no-rows-view
  [{:keys [table]}]
  (let [target-type (:entity-type table)]
    [:div
     {:class "pt-2 flex items-center gap-2"}
     [:p
      {:class "text-sm m-0 text-base-light-text-paler"}
      (loc/tr :no-associated-entity-types-yet
              [(-> target-type
                   (entity-type-names/get-plural :en)
                   string/lower-case)])]]))

(defn content
  [{:keys [table-index ctx] :as props}]
  (let [row-count @(rf/subscribe [::sub/table-entity-count ctx table-index])]
    (if (pos? row-count)
      [table-component props]
      [no-rows-view props])))

(defn view
  [{:keys [table-index ctx] :as props}]
  (let [expanded? @(rf/subscribe [::sub/table-expanded? ctx table-index])
        set-table-expanded (fn [yes?]
                             (rf/dispatch [::event/set-table-expanded ctx {:table-index table-index
                                                                           :expanded? yes?}]))]
    [:div
     [accordion-primitive-root
      {:type "single"
       :asChild true
       :value (when expanded? "table")
       :onValueChange (fn [value]
                        (let [expanded? (not (cu/empty-value? (str value)))]
                          (set-table-expanded expanded?)))
       :collapsible true}
      [accordion-item
       {:value "table"
        :asChild true}
       [:div
        {:class "p-3 flex flex-col items-start border border-solid border-base-light-border-paler rounded"}
        [header props]
        [accordion-content
         [content props]]]]]]))
